import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MobileNav } from '../components/MobileNav';
import { 
  Sparkles, Users, Calendar, Phone, Mail, 
  Search, Loader2, User, FileText, RefreshCw, Heart, Star, Lock, Eye, EyeOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple password for admin access - Karine can change this
const ADMIN_PASSWORD = "Karine2024!";

export const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Check if already authenticated (stored in session)
  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/contact/inquiries`)
      ]);
      setUsers(usersRes.data);
      setInquiries(inquiriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  const filteredInquiries = inquiries.filter(inquiry => 
    inquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inquiry.phone?.includes(searchTerm)
  );

  const lookingForLabels = {
    'love': 'Romantic Love / Soulmate',
    'connection': 'Spiritual Connection',
    'friendship': 'Kindred Spirits',
    'guidance': 'Life Guidance'
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen swirl-bg flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="ethereal-card rounded-3xl p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 mb-6 glow-purple">
                <Lock className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-mystical text-3xl text-[#2D1B3D] font-bold mb-2">Admin Access</h1>
              <p className="text-[#6D5A7D]">Enter your password to view the database</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-14 pr-12 text-lg"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8B5CF6] hover:text-[#D4AF37]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {error && (
                <p className="text-red-500 text-center text-sm">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full h-14 rounded-full btn-magic text-lg font-bold"
                data-testid="admin-login-btn"
              >
                Access Database
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-[#8B5CF6] hover:text-[#D4AF37] font-semibold">
                ← Back to Home
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Main Admin Dashboard
  return (
    <div className="min-h-screen swirl-bg">
      <MobileNav />

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 mb-6 glow-purple">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-mystical text-4xl md:text-5xl text-[#2D1B3D] font-bold mb-4">
            Your Matchmaking Database
          </h1>
          <p className="text-[#4A3A5A] text-lg mb-4">
            All your potential matches and inquiries in one place
          </p>
          <button
            onClick={handleLogout}
            className="text-[#8B5CF6] hover:text-[#D4AF37] text-sm font-semibold"
          >
            🔒 Logout
          </button>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center gap-4 mb-8"
        >
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-full font-bold transition-all ${
              activeTab === 'users' 
                ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg' 
                : 'bg-white/70 text-[#5B21B6] hover:bg-white'
            }`}
            data-testid="tab-users"
          >
            <Heart className="w-5 h-5 inline mr-2" />
            Registered Profiles ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('inquiries')}
            className={`px-6 py-3 rounded-full font-bold transition-all ${
              activeTab === 'inquiries' 
                ? 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg' 
                : 'bg-white/70 text-[#5B21B6] hover:bg-white'
            }`}
            data-testid="tab-inquiries"
          >
            <Mail className="w-5 h-5 inline mr-2" />
            Contact Inquiries ({inquiries.length})
          </button>
        </motion.div>

        {/* Search and Refresh */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4 mb-8"
        >
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#8B5CF6] w-5 h-5" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 bg-white/70 border-purple-200 rounded-xl"
              data-testid="search-input"
            />
          </div>
          <Button
            onClick={fetchData}
            className="rounded-xl btn-magic px-6 h-12"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Refresh
          </Button>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-10 h-10 text-[#8B5CF6] animate-spin" />
          </div>
        ) : (
          <>
            {/* Registered Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {filteredUsers.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-20 ethereal-card rounded-3xl"
                  >
                    <Users className="w-16 h-16 text-[#8B5CF6]/50 mx-auto mb-4" />
                    <h3 className="font-mystical text-2xl text-[#2D1B3D] mb-2">
                      {searchTerm ? 'No matches found' : 'No registered users yet'}
                    </h3>
                    <p className="text-[#6D5A7D]">
                      Users will appear here when they create profiles
                    </p>
                  </motion.div>
                ) : (
                  filteredUsers.map((user, index) => (
                    <motion.div
                      key={user.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="ethereal-card rounded-2xl p-6"
                      data-testid={`user-card-${index}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {user.avatar_url ? (
                            <img 
                              src={user.avatar_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : user.avatar_url}
                              alt={user.full_name}
                              className="w-20 h-20 rounded-full object-cover border-4 border-[#D4AF37]"
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center text-white font-bold text-2xl border-4 border-[#D4AF37]">
                              {user.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                            <h3 className="font-mystical text-xl text-[#2D1B3D] font-bold">
                              {user.full_name}
                            </h3>
                            <span className="text-sm text-[#8C7A9C]">
                              Joined {formatDate(user.created_at)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                            <a 
                              href={`mailto:${user.email}`}
                              className="flex items-center gap-2 text-[#5D4A6D] hover:text-[#8B5CF6] transition-colors"
                            >
                              <Mail className="w-4 h-4 text-[#8B5CF6]" />
                              <span className="text-sm truncate">{user.email}</span>
                            </a>
                            {user.phone && (
                              <a 
                                href={`tel:${user.phone}`}
                                className="flex items-center gap-2 text-[#5D4A6D] hover:text-[#8B5CF6] transition-colors"
                              >
                                <Phone className="w-4 h-4 text-[#D4AF37]" />
                                <span className="text-sm">{user.phone}</span>
                              </a>
                            )}
                            <div className="flex items-center gap-2 text-[#5D4A6D]">
                              <Calendar className="w-4 h-4 text-[#EC4899]" />
                              <span className="text-sm">{formatDate(user.birth_date)}</span>
                            </div>
                          </div>

                          {/* Looking For */}
                          <div className="flex items-center gap-2 mb-3">
                            <Heart className="w-4 h-4 text-[#EC4899]" />
                            <span className="text-sm text-[#5D4A6D]">
                              Looking for: <strong className="text-[#8B5CF6]">{lookingForLabels[user.looking_for] || user.looking_for}</strong>
                            </span>
                          </div>

                          {/* Numerology Numbers */}
                          {user.numerology && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              <span className="px-3 py-1 bg-[#8B5CF6]/10 rounded-full text-sm text-[#8B5CF6] font-semibold">
                                Life Path: {user.numerology.life_path}
                              </span>
                              <span className="px-3 py-1 bg-[#D4AF37]/10 rounded-full text-sm text-[#D4AF37] font-semibold">
                                Expression: {user.numerology.expression}
                              </span>
                              <span className="px-3 py-1 bg-[#EC4899]/10 rounded-full text-sm text-[#EC4899] font-semibold">
                                Soul Urge: {user.numerology.soul_urge}
                              </span>
                            </div>
                          )}
                          
                          {user.bio && (
                            <div className="bg-[#8B5CF6]/5 rounded-xl p-4">
                              <div className="flex items-start gap-2">
                                <FileText className="w-4 h-4 text-[#8B5CF6] mt-1 flex-shrink-0" />
                                <p className="text-[#4A3A5A] text-sm">{user.bio}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}

            {/* Inquiries Tab */}
            {activeTab === 'inquiries' && (
              <div className="space-y-4">
                {filteredInquiries.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-20 ethereal-card rounded-3xl"
                  >
                    <Mail className="w-16 h-16 text-[#8B5CF6]/50 mx-auto mb-4" />
                    <h3 className="font-mystical text-2xl text-[#2D1B3D] mb-2">
                      {searchTerm ? 'No matches found' : 'No inquiries yet'}
                    </h3>
                    <p className="text-[#6D5A7D]">
                      Inquiries will appear here when people contact you
                    </p>
                  </motion.div>
                ) : (
                  filteredInquiries.map((inquiry, index) => (
                    <motion.div
                      key={inquiry.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="ethereal-card rounded-2xl p-6"
                      data-testid={`inquiry-card-${index}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#EC4899] flex items-center justify-center text-white font-bold text-xl">
                            {inquiry.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                          </div>
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                            <h3 className="font-mystical text-xl text-[#2D1B3D] font-bold">
                              {inquiry.name}
                            </h3>
                            <span className="text-sm text-[#8C7A9C]">
                              {formatDate(inquiry.created_at)}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                            <a 
                              href={`mailto:${inquiry.email}`}
                              className="flex items-center gap-2 text-[#5D4A6D] hover:text-[#8B5CF6] transition-colors"
                            >
                              <Mail className="w-4 h-4 text-[#8B5CF6]" />
                              <span className="text-sm truncate">{inquiry.email}</span>
                            </a>
                            <a 
                              href={`tel:${inquiry.phone}`}
                              className="flex items-center gap-2 text-[#5D4A6D] hover:text-[#8B5CF6] transition-colors"
                            >
                              <Phone className="w-4 h-4 text-[#D4AF37]" />
                              <span className="text-sm">{inquiry.phone}</span>
                            </a>
                            <div className="flex items-center gap-2 text-[#5D4A6D]">
                              <Calendar className="w-4 h-4 text-[#EC4899]" />
                              <span className="text-sm">{formatDate(inquiry.birthday)}</span>
                            </div>
                          </div>
                          
                          {inquiry.note && (
                            <div className="bg-[#D4AF37]/10 rounded-xl p-4">
                              <div className="flex items-start gap-2">
                                <FileText className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                                <p className="text-[#4A3A5A] text-sm">{inquiry.note}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-12 border-t border-purple-200/50 mt-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="font-mystical text-3xl font-bold" style={{ color: '#5B21B6' }}>Intuitive Matchmaker</span>
            </div>
            <p className="text-[#6D5A7D] text-lg font-light font-whimsy">
              Your matchmaking dashboard
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminPage;
